import { toastState } from "@/atoms/system/toast-state";
import { GLOBAL_ERROR } from "@/common/constant/global-error";
import { usePrompt } from "@/common/custom-hook/use-promt";
import { AdminItemPath } from "@/common/enum/path";
import { CreateItemModal } from "@/components/modal/CreateItemModal";
import { PageWrapper } from "@/components/wrapper/PageWrapper";
import { itemService } from "@/services/item-service";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import validator from "validator";
import { PacakgeItemCreateHeader } from "../../../components/header/PackageItemCreateHeader";
import { TextInputLabel } from "../../../components/label/TextInputLabel";
import { PackageChildItemModal } from "../../../components/modal/PackageChildItemModal";
import { PackageCreatePanel } from "../../../components/panel/PackageCreatePanel";
import { PackageItemChildTable } from "../../../components/table/package-item-main/PackageItemChildTable";
import { ItemDetailLineDto } from "../../../types/dto/item-detail-line-dto";
import { CreatePackagePayload } from "../../../types/payload/create-package-payload";

const initCreatePackagePayload: CreatePackagePayload = {
  name: "",
  files: [],
  details: "",
  features: "",
  modelName: "",
  isCataloged: false,
  items: [],
  memos: [],
};

export default function PackageProductCreatePage() {
  const navigate = useNavigate();
  const [createItemPayload, setCreateItemPayload] =
    useState<CreatePackagePayload>(initCreatePackagePayload);
  const [isOpenCreateItemModal, setIsOpenCreateItemModal] = useState(false);
  const setToast = useSetRecoilState(toastState);
  const [childItems, setChildItems] = useState<ItemDetailLineDto[]>([]);
  const isActive =
    !validator.isEmpty(createItemPayload.name) &&
    !validator.isEmpty(createItemPayload.details) &&
    !validator.isEmpty(createItemPayload.features) &&
    !validator.isEmpty(createItemPayload.modelName) &&
    childItems.length > 1;

  const [blocked, setBlocked] = useState(true);

  const [isOpenChildItemModal, setIsOpenChildItemModal] = useState(false);

  const handleMemoChange = (index: number, value: string) => {
    setCreateItemPayload((prevPayload) => {
      const newMemos = [...prevPayload.memos];
      newMemos[index] = value;
      return {
        ...prevPayload,
        memos: newMemos,
      };
    });
  };

  const handleAddMemo = () => {
    setCreateItemPayload((prevPayload) => {
      if (prevPayload.memos.length >= 10) return prevPayload;

      return {
        ...prevPayload,
        memos: [...prevPayload.memos, ""],
      };
    });
  };

  const handleRemoveMemo = (index: number) => {
    setCreateItemPayload((prevPayload) => ({
      ...prevPayload,
      memos: prevPayload.memos.filter((_, i) => i !== index),
    }));
  };

  const handleOpenChildItemModal = () => {
    setIsOpenChildItemModal(true);
  };

  const handleCloseChildItemModal = () => {
    setIsOpenChildItemModal(false);
  };

  const handleOpenCreateItemModal = () => {
    setIsOpenCreateItemModal(true);
  };

  const handleCloseCreateItemModal = () => {
    setIsOpenCreateItemModal(false);
  };

  const handleFileChange = (files: File[]) => {
    setCreateItemPayload((prevPayload) => ({
      ...prevPayload,
      files: files,
    }));
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setCreateItemPayload((prevPayload) => ({
      ...prevPayload,
      [name]: value,
    }));
  };

  const handleCatalogChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setCreateItemPayload((prevPayload) => ({
      ...prevPayload,
      isCataloged: value === "YES",
    }));
  };

  const handleCreateButtonClick = async () => {
    try {
      const createResponse = await itemService.createPackage({
        name: createItemPayload.name,
        details: createItemPayload.details,
        features: createItemPayload.features,
        modelName: createItemPayload.modelName,
        isCataloged: createItemPayload.isCataloged,
        items: childItems.map((line) => {
          return {
            itemId: line.childItem.id,
            quantity: line.quantity,
          };
        }),
        files: createItemPayload.files,
        memos: createItemPayload.memos,
      });
      if (!createResponse.ok) {
        const errorArray = Object.values(
          createResponse.data.aggregateErrors || {}
        );
        if (errorArray.length > 0) {
          alert(`${errorArray[0]}`);
        } else {
          alert(GLOBAL_ERROR);
        }
        return;
      }
      // const getByIdResponse = await itemService.getById({
      //   id: createResponse.data.id,
      // });
      // setCreateItemPayload({
      //   ...createItemPayload,
      //   name: getByIdResponse.data.name,
      //   details: getByIdResponse.data.details,
      //   features: getByIdResponse.data.features,
      //   modelName: getByIdResponse.data.modelName,
      //   isCataloged: getByIdResponse.data.isCataloged,
      // });
      setToast({
        show: true,
        messages: [`상품이 등록되었습니다.`],
      });
      handleCloseCreateItemModal();
      setBlocked(false);
      navigate(AdminItemPath.PACKAGE_PRODUCT);
    } catch (error) {
      console.error("Error creating item:", error);
    }
  };

  const updateCategoryId = (categoryId: number) => {
    setCreateItemPayload((prevPayload) => ({
      ...prevPayload,
      categoryId,
    }));
  };

  const addQuantity = (id: number) => {
    setChildItems((prevItems) => {
      return prevItems.map((item) => {
        if (item.childItem.id === id) {
          return {
            ...item,
            quantity: item.quantity + 1,
          };
        }
        return item;
      });
    });
  };

  const subtractQuantity = (id: number) => {
    setChildItems((prevItems) => {
      return prevItems.map((item) => {
        if (item.childItem.id === id) {
          return {
            ...item,
            quantity: Math.max(item.quantity - 1, 1),
          };
        }
        return item;
      });
    });
  };

  const removeItem = (id: number) => {
    setChildItems((prevItems) => {
      return prevItems.filter((item) => item.childItem.id !== id);
    });
  };

  usePrompt(
    "사이트에서 나가시겠습니까? 변경사항이 저장되지 않을 수 있습니다.",
    blocked
  );

  return (
    <PageWrapper>
      <div className="flex flex-col w-[1648px]">
        <PacakgeItemCreateHeader
          isActive={isActive}
          handleButtonClick={handleOpenCreateItemModal}
        />
        <div className="flex flex-col gap-[24px]">
          <PackageCreatePanel
            payload={createItemPayload}
            handleFileChange={handleFileChange}
            handleChange={handleChange}
            handleCatalogChange={handleCatalogChange}
            handleMemoChange={handleMemoChange}
            handleAddMemo={handleAddMemo}
            handleRemoveMemo={handleRemoveMemo}
          />
          <div className="flex justify-center items-center p-[24px] bg-white rounded-[8px]">
            <div className="flex flex-col justify-center gap-[16px]">
              <TextInputLabel required>패키지 상품</TextInputLabel>
              <PackageItemChildTable
                items={childItems}
                handleOpenChildItemModal={handleOpenChildItemModal}
                addQuantity={addQuantity}
                subtractQuantity={subtractQuantity}
                removeItem={removeItem}
              />
            </div>
          </div>
        </div>
      </div>
      {isOpenCreateItemModal && (
        <CreateItemModal
          handleSubmit={handleCreateButtonClick}
          closeModal={handleCloseCreateItemModal}
        />
      )}
      {isOpenChildItemModal && (
        <PackageChildItemModal
          // childItems={createItemPayload.items}
          childDetailItems={childItems.map((line) => {
            return line.childItem;
          })}
          setChildItems={(items) => {
            const newlines = items.map((item) => {
              return {
                quantity: 1,
                childItem: item,
              };
            });
            setChildItems(newlines);
          }}
          closeModal={handleCloseChildItemModal}
        />
      )}
    </PageWrapper>
  );
}
